import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    buildStartOutreachForShiftsAction,
    type DynamicWidgetActionRequest,
    type DynamicWidgetActionState,
    type DynamicWidgetActionStateResolver,
    IDLE_DYNAMIC_WIDGET_ACTION_STATE,
    reduceDynamicWidgetActionState,
    START_OUTREACH_MAX_SHIFT_COUNT,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import {
    captureDynamicWidgetMetric,
    type DynamicWidgetMetricEvent,
    getDynamicWidgetMetricTags,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type ShiftListWidgetModel,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { getActionableShiftRows } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_model';
import { EVENTS } from '@apps/web/lib/events';

type LocalActionSubmission = {
    readonly action: DynamicWidgetActionRequest;
    readonly status: 'submitting' | 'failed';
    readonly error: unknown;
};

export type RenderedShiftActionState = {
    readonly state: DynamicWidgetActionState;
    readonly isSubmitting: boolean;
};

function actionSubmissionKey(rows: ReadonlyArray<ShiftListWidgetRow>): string {
    return rows.flatMap((row) => row.occurrenceIds).join(':');
}

export function useShiftWidgetActions({
    model,
    onWidgetAction,
    getActionState,
}: {
    readonly model: ShiftListWidgetModel;
    readonly onWidgetAction?: (
        action: DynamicWidgetActionRequest,
    ) => Promise<boolean | void> | boolean | void;
    readonly getActionState?: DynamicWidgetActionStateResolver;
}) {
    const posthog = usePostHog();
    const [localSubmissions, setLocalSubmissions] = useState<
        Record<string, LocalActionSubmission>
    >({});
    const reportedActionMetrics = useRef<Set<string>>(new Set());
    const actionSubmittedAtMs = useRef<Map<string, number>>(new Map());
    const metricTags = useMemo(
        () => getDynamicWidgetMetricTags(model),
        [model],
    );
    const captureActionMetricOnce = useCallback(
        ({
            event,
            actionId,
            errorCode,
            errorClass,
            durationMs,
        }: {
            readonly event: DynamicWidgetMetricEvent;
            readonly actionId: string;
            readonly errorCode?: string;
            readonly errorClass?: string;
            readonly durationMs?: number;
        }) => {
            const key = `${event}:${actionId}`;
            if (reportedActionMetrics.current.has(key)) {
                return;
            }
            reportedActionMetrics.current.add(key);
            captureDynamicWidgetMetric(posthog, event, {
                ...metricTags,
                ...(errorCode === undefined ? {} : { errorCode }),
                ...(errorClass === undefined ? {} : { errorClass }),
                ...(durationMs === undefined ? {} : { durationMs }),
            });
        },
        [metricTags, posthog],
    );

    const getRenderedActionState = useCallback(
        (rows: ReadonlyArray<ShiftListWidgetRow>): RenderedShiftActionState => {
            const shiftIds = rows.flatMap((row) => row.occurrenceIds);
            const committedState =
                getActionState?.(model.widgetId, shiftIds) ??
                IDLE_DYNAMIC_WIDGET_ACTION_STATE;
            if (committedState.status !== 'idle') {
                return {
                    state: committedState,
                    isSubmitting: false,
                };
            }
            const localSubmission =
                localSubmissions[actionSubmissionKey(rows)] ?? null;
            if (localSubmission === null) {
                return {
                    state: committedState,
                    isSubmitting: false,
                };
            }
            if (localSubmission.status === 'submitting') {
                return {
                    state: committedState,
                    isSubmitting: true,
                };
            }
            return {
                state: reduceDynamicWidgetActionState(
                    localSubmission.action.id,
                    [],
                    {
                        action: localSubmission.action,
                        error: localSubmission.error,
                    },
                ),
                isSubmitting: false,
            };
        },
        [getActionState, localSubmissions, model.widgetId],
    );

    useEffect(() => {
        if (getActionState === undefined) {
            return;
        }
        setLocalSubmissions((current) => {
            const retainedSubmissions = Object.fromEntries(
                Object.entries(current).filter(([, submission]) => {
                    const committedState = getActionState(
                        model.widgetId,
                        submission.action.shift_ids,
                    );
                    return committedState.status === 'idle';
                }),
            );
            return Object.keys(retainedSubmissions).length ===
                Object.keys(current).length
                ? current
                : retainedSubmissions;
        });
    }, [getActionState, model.widgetId]);

    const requestShiftOutreach = useCallback(
        async (
            rows: ReadonlyArray<ShiftListWidgetRow>,
            displayText: string,
        ) => {
            if (
                onWidgetAction === undefined ||
                rows.every((row) => row.occurrenceIds.length === 0) ||
                new Set(rows.flatMap((row) => row.occurrenceIds)).size >
                    START_OUTREACH_MAX_SHIFT_COUNT
            ) {
                return;
            }
            const action = buildStartOutreachForShiftsAction({
                model,
                rows,
                displayText,
            });
            actionSubmittedAtMs.current.set(action.id, Date.now());
            captureActionMetricOnce({
                event: EVENTS.DYNAMIC_WIDGET_ACTION_SUBMITTED,
                actionId: action.id,
            });
            const key = actionSubmissionKey(rows);
            setLocalSubmissions((current) => ({
                ...current,
                [key]: { action, status: 'submitting', error: null },
            }));
            try {
                const submitted = await onWidgetAction(action);
                if (submitted === false) {
                    captureActionMetricOnce({
                        event: EVENTS.DYNAMIC_WIDGET_ACTION_FAILED,
                        actionId: action.id,
                        errorClass: 'submission_rejected',
                    });
                    captureActionMetricOnce({
                        event: EVENTS.DYNAMIC_WIDGET_ACTION_TERMINAL_TIMING,
                        actionId: action.id,
                        durationMs:
                            Date.now() -
                            (actionSubmittedAtMs.current.get(action.id) ??
                                Date.now()),
                    });
                    actionSubmittedAtMs.current.delete(action.id);
                    setLocalSubmissions((current) => ({
                        ...current,
                        [key]: { action, status: 'failed', error: null },
                    }));
                    return;
                }
                setLocalSubmissions((current) => {
                    if (!(key in current)) {
                        return current;
                    }
                    return Object.fromEntries(
                        Object.entries(current).filter(
                            ([submissionKey]) => submissionKey !== key,
                        ),
                    );
                });
            } catch (error) {
                const failedState = reduceDynamicWidgetActionState(
                    action.id,
                    [],
                    { action, error },
                );
                captureActionMetricOnce({
                    event: EVENTS.DYNAMIC_WIDGET_ACTION_FAILED,
                    actionId: action.id,
                    ...(failedState.errorCode === null
                        ? { errorClass: 'submission_failed' }
                        : {
                              errorCode: failedState.errorCode,
                              errorClass: 'action_rejected',
                          }),
                });
                captureActionMetricOnce({
                    event: EVENTS.DYNAMIC_WIDGET_ACTION_TERMINAL_TIMING,
                    actionId: action.id,
                    durationMs:
                        Date.now() -
                        (actionSubmittedAtMs.current.get(action.id) ??
                            Date.now()),
                });
                actionSubmittedAtMs.current.delete(action.id);
                setLocalSubmissions((current) => ({
                    ...current,
                    [key]: { action, status: 'failed', error },
                }));
            }
        },
        [captureActionMetricOnce, model, onWidgetAction],
    );

    const bulkRows = getActionableShiftRows(model.rows).filter((row) =>
        row.occurrenceIds.some((shiftId) =>
            model.bulkShiftIds.includes(shiftId),
        ),
    );
    const terminalActionStates = [
        ...model.rows.map((row) => getRenderedActionState([row]).state),
        getRenderedActionState(bulkRows).state,
    ].filter(
        (state) =>
            state.action !== null &&
            (state.status === 'succeeded' || state.status === 'failed'),
    );

    useEffect(() => {
        terminalActionStates.forEach((state) => {
            const actionId = state.action?.id;
            if (actionId === undefined) {
                return;
            }
            if (state.status === 'succeeded') {
                captureActionMetricOnce({
                    event: EVENTS.DYNAMIC_WIDGET_ACTION_SUCCEEDED,
                    actionId,
                });
                const submittedAt = actionSubmittedAtMs.current.get(actionId);
                if (submittedAt !== undefined) {
                    captureActionMetricOnce({
                        event: EVENTS.DYNAMIC_WIDGET_ACTION_TERMINAL_TIMING,
                        actionId,
                        durationMs: Date.now() - submittedAt,
                    });
                    actionSubmittedAtMs.current.delete(actionId);
                }
                return;
            }
            captureActionMetricOnce({
                event: EVENTS.DYNAMIC_WIDGET_ACTION_FAILED,
                actionId,
                ...(state.errorCode === null
                    ? { errorClass: 'action_failed' }
                    : {
                          errorCode: state.errorCode,
                          errorClass: 'action_rejected',
                      }),
            });
            const submittedAt = actionSubmittedAtMs.current.get(actionId);
            if (submittedAt !== undefined) {
                captureActionMetricOnce({
                    event: EVENTS.DYNAMIC_WIDGET_ACTION_TERMINAL_TIMING,
                    actionId,
                    durationMs: Date.now() - submittedAt,
                });
                actionSubmittedAtMs.current.delete(actionId);
            }
        });
    }, [captureActionMetricOnce, terminalActionStates]);

    return {
        metricTags,
        getRenderedActionState,
        requestShiftOutreach,
    };
}
